<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useSoftphone } from 'dashboard/composables/useSoftphone';
import SipColleaguesAPI from 'dashboard/api/sipColleagues';
import SoftphoneAPI from 'dashboard/api/softphone';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const {
  registration,
  call,
  isMuted,
  isOnHold,
  isTransferring,
  callerInfo,
  quality,
  durationSeconds,
  lastError,
  connect,
  reconnect,
  startCall,
  answer,
  hangup,
  toggleMute,
  toggleHold,
  transfer,
  sendDtmf,
} = useSoftphone();

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

// Colleagues' presence and the call lists only refresh while the panel is open.
const COLLEAGUES_REFRESH_MS = 15000;
const CALLS_REFRESH_MS = 30000;

const isOpen = ref(false);
const tab = ref('dial'); // dial | recent | callbacks
const dialInput = ref('');
const colleagues = ref([]);
const recentCalls = ref([]);
const callbacks = ref([]);
const callsError = ref(false);
const showTransfer = ref(false);
const transferInput = ref('');
let colleaguesTimer = null;
let callsTimer = null;

const fetchColleagues = async () => {
  try {
    const { data } = await SipColleaguesAPI.get();
    colleagues.value = Array.isArray(data) ? data : [];
  } catch (error) {
    /* noop */
  }
};

const fetchCalls = async () => {
  try {
    const { data } = await SoftphoneAPI.calls();
    recentCalls.value = data.mine;
    callbacks.value = data.callbacks;
    callsError.value = false;
  } catch (error) {
    callsError.value = true;
  }
};

const onlineColleagues = computed(() =>
  colleagues.value
    .filter(c => c.extension && c.availability && c.availability !== 'offline')
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
);

const callColleague = colleague => {
  if (!colleague.extension || registration.value !== 'registered') return;
  dialInput.value = colleague.extension;
  startCall(colleague.extension);
};

const callNumber = dial => {
  if (!dial || registration.value !== 'registered') return;
  dialInput.value = dial;
  startCall(dial);
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const statusDotClass = computed(
  () =>
    ({
      registered: 'bg-n-teal-9',
      connecting: 'bg-n-amber-9',
      failed: 'bg-n-ruby-9',
      idle: 'bg-n-slate-9',
    })[registration.value] || 'bg-n-slate-9'
);

const statusLabel = computed(
  () =>
    ({
      registered: 'Conectado',
      connecting: 'Conectando…',
      failed: 'Falha na conexão',
      idle: 'Offline',
    })[registration.value] || 'Offline'
);

const QUALITY = {
  good: { label: 'Boa', icon: 'i-lucide-signal-high', class: 'text-n-teal-11' },
  unstable: {
    label: 'Instável',
    icon: 'i-lucide-signal-medium',
    class: 'text-n-amber-11',
  },
  bad: { label: 'Ruim', icon: 'i-lucide-signal-low', class: 'text-n-ruby-11' },
};

const qualityView = computed(
  () => quality.value && QUALITY[quality.value.level]
);
const qualityTitle = computed(() =>
  quality.value
    ? `Atraso ${quality.value.rttMs} ms · perda ${quality.value.lossPct}% · variação ${quality.value.jitterMs} ms`
    : ''
);

const inCall = computed(() => !!call.value);
const isActive = computed(() => call.value?.state === 'active');
const isIncomingRinging = computed(
  () => call.value?.direction === 'in' && call.value?.state === 'ringing'
);
const isOutgoingRinging = computed(
  () => call.value?.direction === 'out' && call.value?.state === 'ringing'
);

// Name first (KALL-AI contact, then IXC client), the number under it.
const callerName = computed(
  () => callerInfo.value?.contact?.name || callerInfo.value?.ixc?.name || ''
);
const callerNumber = computed(
  () => callerInfo.value?.display || call.value?.number || ''
);

const openCallerInSyllai = () => {
  const contact = callerInfo.value?.contact;
  if (!contact) return;
  if (contact.conversation_id) {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: accountId.value,
        conversation_id: contact.conversation_id,
      },
    });
  } else {
    router.push({
      name: 'contacts_edit',
      params: { accountId: accountId.value, contactId: contact.id },
    });
  }
};

const formatDuration = total => {
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const formattedDuration = computed(() => formatDuration(durationSeconds.value));

const formatWhen = iso => {
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const recentIcon = item => {
  if (item.direcao === 'saida') {
    return 'i-lucide-phone-outgoing text-n-slate-10';
  }
  if (item.direcao === 'entrada' && !item.atendida) {
    return 'i-lucide-phone-missed text-n-ruby-11';
  }
  return 'i-lucide-phone-incoming text-n-teal-11';
};

const TABS = [
  { value: 'dial', label: 'Teclado' },
  { value: 'recent', label: 'Recentes' },
  { value: 'callbacks', label: 'Retornar' },
];

const onKey = key => {
  if (isActive.value) {
    sendDtmf(key);
    return;
  }
  dialInput.value += key;
};

const callNow = () => {
  const number = dialInput.value.trim();
  if (!number) return;
  startCall(number);
};

const doTransfer = target => {
  const number = (target || transferInput.value).trim();
  if (!number) return;
  transfer(number);
};

const togglePanel = () => {
  isOpen.value = !isOpen.value;
  if (
    isOpen.value &&
    (registration.value === 'failed' || registration.value === 'idle')
  ) {
    reconnect();
  }
};

watch(call, value => {
  if (value?.direction === 'in') isOpen.value = true;
  if (!value) {
    dialInput.value = '';
    showTransfer.value = false;
    transferInput.value = '';
    if (isOpen.value) fetchCalls();
  }
});

watch(isOpen, open => {
  if (open) {
    fetchColleagues();
    fetchCalls();
    colleaguesTimer = setInterval(fetchColleagues, COLLEAGUES_REFRESH_MS);
    callsTimer = setInterval(fetchCalls, CALLS_REFRESH_MS);
  } else {
    clearInterval(colleaguesTimer);
    clearInterval(callsTimer);
  }
});

onBeforeUnmount(() => {
  clearInterval(colleaguesTimer);
  clearInterval(callsTimer);
});

onMounted(connect);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="relative w-full">
    <template v-if="registration !== 'unconfigured'">
      <button
        class="flex items-center w-full gap-2 px-2 py-1.5 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        :class="isCollapsed ? 'justify-center' : ''"
        title="Softphone SIP"
        @click="togglePanel"
      >
        <span class="relative flex items-center justify-center size-5">
          <span class="size-4 i-lucide-phone" />
          <span
            class="absolute -top-0.5 -right-0.5 rounded-full size-2 ring-2 ring-n-background"
            :class="statusDotClass"
          />
        </span>
        <span v-if="!isCollapsed" class="text-sm truncate">Telefone</span>
        <span
          v-if="!isCollapsed && isActive"
          class="ml-auto text-xs tabular-nums text-n-teal-11"
        >
          {{ formattedDuration }}
        </span>
      </button>

      <div
        v-if="isOpen"
        class="absolute left-0 z-50 p-3 mb-2 border shadow-lg w-72 bottom-full rounded-xl bg-n-solid-1 border-n-weak"
      >
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-1.5">
            <span class="rounded-full size-2" :class="statusDotClass" />
            <span class="text-xs font-medium text-n-slate-11">
              {{ statusLabel }}
            </span>
          </div>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="isOpen = false"
          >
            <span class="size-4 i-lucide-x" />
          </button>
        </div>

        <div v-if="registration !== 'registered'" class="py-3 text-center">
          <p class="mb-1 text-xs text-n-slate-10">{{ statusLabel }}</p>
          <p
            v-if="lastError && registration === 'failed'"
            class="mb-2 text-xs break-words text-n-ruby-11"
          >
            {{ lastError }}
          </p>
          <button
            v-if="registration === 'failed' || registration === 'idle'"
            class="px-3 py-1.5 text-sm rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
            @click="reconnect"
          >
            Reconectar
          </button>
        </div>

        <template v-else-if="inCall">
          <div class="py-2 text-center">
            <p v-if="isIncomingRinging" class="text-xs text-n-slate-10">
              Chamada recebida
            </p>
            <p class="text-lg font-semibold truncate text-n-slate-12">
              {{ callerName || callerNumber || 'Desconhecido' }}
            </p>
            <p v-if="callerName" class="text-xs tabular-nums text-n-slate-11">
              {{ callerNumber }}
            </p>
            <p v-if="callerInfo?.ixc" class="mt-0.5 text-xs text-n-slate-10">
              Cliente IXC
              <span
                :class="
                  callerInfo.ixc.active ? 'text-n-teal-11' : 'text-n-ruby-11'
                "
              >
                · {{ callerInfo.ixc.active ? 'ativo' : 'inativo' }}
              </span>
            </p>
            <button
              v-if="callerInfo?.contact"
              class="inline-flex items-center gap-1 mt-1 text-xs text-n-blue-11 hover:underline"
              @click="openCallerInSyllai"
            >
              <span class="size-3.5 i-lucide-message-square" />
              {{
                callerInfo.contact.conversation_id
                  ? 'Abrir conversa'
                  : 'Abrir contato'
              }}
            </button>
            <p
              v-if="isActive"
              class="flex items-center justify-center gap-2 mt-1 text-sm tabular-nums text-n-teal-11"
            >
              {{ formattedDuration }}
              <span v-if="isOnHold" class="text-xs text-n-amber-11">
                em espera
              </span>
              <span
                v-if="qualityView"
                class="inline-flex items-center gap-0.5 text-xs"
                :class="qualityView.class"
                :title="qualityTitle"
              >
                <span class="size-3.5" :class="qualityView.icon" />
                {{ qualityView.label }}
              </span>
            </p>
            <p
              v-if="quality?.level === 'bad'"
              class="mt-1 text-xs text-n-ruby-11"
            >
              Conexão ruim: a voz pode falhar. Confira sua internet.
            </p>
            <p v-else-if="isOutgoingRinging" class="text-xs text-n-slate-10">
              Chamando…
            </p>
          </div>

          <p
            v-if="lastError"
            class="mb-2 text-xs text-center break-words text-n-ruby-11"
          >
            {{ lastError }}
          </p>

          <div v-if="isIncomingRinging" class="flex gap-2">
            <button
              class="flex items-center justify-center flex-1 gap-1 py-2 text-sm font-medium text-white rounded-lg bg-n-teal-9 hover:bg-n-teal-10"
              @click="answer"
            >
              <span class="size-4 i-lucide-phone" /> Atender
            </button>
            <button
              class="flex items-center justify-center flex-1 gap-1 py-2 text-sm font-medium text-white rounded-lg bg-n-ruby-9 hover:bg-n-ruby-10"
              @click="hangup"
            >
              <span class="size-4 i-lucide-phone-off" /> Rejeitar
            </button>
          </div>

          <template v-else>
            <div v-if="showTransfer" class="mb-2">
              <p
                class="mb-1 text-[10px] font-medium tracking-wide uppercase text-n-slate-10"
              >
                Transferir para
              </p>
              <div
                v-if="onlineColleagues.length"
                class="flex flex-col gap-0.5 mb-1 overflow-y-auto max-h-28"
              >
                <button
                  v-for="c in onlineColleagues"
                  :key="c.id"
                  class="flex items-center gap-2 px-2 py-1 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
                  :disabled="isTransferring"
                  @click="doTransfer(c.extension)"
                >
                  <span class="truncate">{{ c.name }}</span>
                  <span class="ml-auto text-xs text-n-slate-10">
                    {{ c.extension }}
                  </span>
                </button>
              </div>
              <div class="flex gap-1">
                <input
                  v-model="transferInput"
                  type="tel"
                  placeholder="Ramal ou número"
                  class="flex-1 min-w-0 px-2 py-1.5 text-sm rounded-md bg-n-alpha-2 text-n-slate-12 focus:outline-none"
                  @keyup.enter="doTransfer()"
                />
                <button
                  class="px-2 text-sm font-medium text-white rounded-md bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
                  :disabled="isTransferring || !transferInput.trim()"
                  @click="doTransfer()"
                >
                  {{ isTransferring ? 'Transferindo…' : 'Transferir' }}
                </button>
              </div>
            </div>

            <div
              v-else-if="isActive && !isOnHold"
              class="grid grid-cols-3 gap-1 mb-2"
            >
              <button
                v-for="key in KEYS"
                :key="key"
                class="py-1.5 text-sm rounded-md text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
                @click="onKey(key)"
              >
                {{ key }}
              </button>
            </div>

            <div v-if="isActive" class="grid grid-cols-3 gap-1 mb-2">
              <button
                class="flex flex-col items-center gap-0.5 py-1.5 text-xs rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3"
                :class="isMuted ? 'text-n-amber-11' : 'text-n-slate-11'"
                @click="toggleMute"
              >
                <span
                  class="size-4"
                  :class="isMuted ? 'i-lucide-mic-off' : 'i-lucide-mic'"
                />
                {{ isMuted ? 'Mudo' : 'Mutar' }}
              </button>
              <button
                class="flex flex-col items-center gap-0.5 py-1.5 text-xs rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3"
                :class="isOnHold ? 'text-n-amber-11' : 'text-n-slate-11'"
                @click="toggleHold"
              >
                <span
                  class="size-4"
                  :class="isOnHold ? 'i-lucide-play' : 'i-lucide-pause'"
                />
                {{ isOnHold ? 'Retomar' : 'Espera' }}
              </button>
              <button
                class="flex flex-col items-center gap-0.5 py-1.5 text-xs rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3"
                :class="showTransfer ? 'text-n-blue-11' : 'text-n-slate-11'"
                @click="showTransfer = !showTransfer"
              >
                <span class="size-4 i-lucide-arrow-right-left" />
                Transferir
              </button>
            </div>

            <button
              class="flex items-center justify-center w-full gap-1 py-2 text-sm font-medium text-white rounded-lg bg-n-ruby-9 hover:bg-n-ruby-10"
              @click="hangup"
            >
              <span class="size-4 i-lucide-phone-off" /> Desligar
            </button>
          </template>
        </template>

        <template v-else>
          <div class="flex gap-1 p-0.5 mb-2 rounded-lg bg-n-alpha-2">
            <button
              v-for="option in TABS"
              :key="option.value"
              class="flex-1 py-1 text-xs font-medium rounded-md"
              :class="
                tab === option.value
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="tab = option.value"
            >
              {{ option.label }}
              <span
                v-if="option.value === 'callbacks' && callbacks.length"
                class="px-1 ms-0.5 text-[10px] text-white rounded-full bg-n-ruby-9"
              >
                {{ callbacks.length }}
              </span>
            </button>
          </div>

          <p
            v-if="lastError"
            class="mb-2 text-xs text-center break-words text-n-ruby-11"
          >
            {{ lastError }}
          </p>

          <template v-if="tab === 'dial'">
            <div v-if="onlineColleagues.length" class="mb-2">
              <p
                class="mb-1 text-[10px] font-medium tracking-wide uppercase text-n-slate-10"
              >
                Colegas online
              </p>
              <div class="flex flex-col gap-0.5 overflow-y-auto max-h-28">
                <button
                  v-for="c in onlineColleagues"
                  :key="c.id"
                  class="flex items-center gap-2 px-2 py-1 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-2"
                  @click="callColleague(c)"
                >
                  <span
                    class="rounded-full size-2 shrink-0"
                    :class="
                      c.availability === 'online'
                        ? 'bg-n-teal-9'
                        : 'bg-n-amber-9'
                    "
                  />
                  <span class="truncate">{{ c.name }}</span>
                  <span class="ml-auto text-xs text-n-slate-10">{{
                    c.extension
                  }}</span>
                </button>
              </div>
            </div>
            <input
              v-model="dialInput"
              type="tel"
              placeholder="Número / ramal"
              class="w-full px-2 py-1.5 mb-2 text-sm text-center rounded-md bg-n-alpha-2 text-n-slate-12 focus:outline-none"
              @keyup.enter="callNow"
            />
            <div class="grid grid-cols-3 gap-1 mb-2">
              <button
                v-for="key in KEYS"
                :key="key"
                class="py-2 text-sm rounded-md text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
                @click="onKey(key)"
              >
                {{ key }}
              </button>
            </div>
            <button
              class="flex items-center justify-center w-full gap-1 py-2 text-sm font-medium text-white rounded-lg bg-n-teal-9 hover:bg-n-teal-10 disabled:opacity-50"
              :disabled="!dialInput.trim()"
              @click="callNow"
            >
              <span class="size-4 i-lucide-phone" /> Ligar
            </button>
          </template>

          <div v-else class="flex flex-col gap-0.5 overflow-y-auto max-h-80">
            <p
              v-if="callsError"
              class="py-4 text-xs text-center text-n-ruby-11"
            >
              Não foi possível carregar as ligações.
            </p>
            <template v-else-if="tab === 'recent'">
              <p
                v-if="!recentCalls.length"
                class="py-4 text-xs text-center text-n-slate-10"
              >
                Nenhuma ligação sua ainda.
              </p>
              <button
                v-for="item in recentCalls"
                :key="item.id"
                class="flex items-center gap-2 px-2 py-1.5 text-start rounded-md hover:bg-n-alpha-2 disabled:opacity-60"
                :disabled="!item.dial"
                :title="item.dial ? `Ligar para ${item.display}` : ''"
                @click="callNumber(item.dial)"
              >
                <span class="size-4 shrink-0" :class="recentIcon(item)" />
                <span class="flex flex-col min-w-0">
                  <span class="text-sm truncate text-n-slate-12">
                    {{ item.contact_name || item.display || 'Ligação interna' }}
                  </span>
                  <span
                    v-if="item.contact_name"
                    class="text-xs truncate tabular-nums text-n-slate-10"
                  >
                    {{ item.display }}
                  </span>
                </span>
                <span
                  class="flex flex-col items-end ml-auto text-xs tabular-nums text-n-slate-10 shrink-0"
                >
                  <span>{{ formatWhen(item.inicio) }}</span>
                  <span v-if="item.duracao_s != null">
                    {{ formatDuration(item.duracao_s) }}
                  </span>
                </span>
              </button>
            </template>
            <template v-else>
              <p class="mb-1 text-xs text-n-slate-10">
                Clientes que ligaram nos últimos 2 dias e ninguém atendeu. Somem
                da lista quando uma ligação com eles é atendida.
              </p>
              <p
                v-if="!callbacks.length"
                class="py-4 text-xs text-center text-n-slate-10"
              >
                Ninguém para retornar.
              </p>
              <div
                v-for="item in callbacks"
                :key="item.dial"
                class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2"
              >
                <span
                  class="size-4 shrink-0 i-lucide-phone-missed text-n-ruby-11"
                />
                <span class="flex flex-col min-w-0">
                  <span class="text-sm truncate text-n-slate-12">
                    {{ item.contact_name || item.display }}
                  </span>
                  <span class="text-xs truncate tabular-nums text-n-slate-10">
                    {{ item.contact_name ? `${item.display} · ` : '' }}
                    {{ formatWhen(item.ultima) }}
                    {{
                      item.tentativas > 1
                        ? `· ${item.tentativas} tentativas`
                        : ''
                    }}
                  </span>
                </span>
                <button
                  class="flex items-center justify-center ml-auto rounded-full size-7 shrink-0 text-n-teal-11 bg-n-alpha-2 hover:bg-n-alpha-3"
                  :title="`Ligar para ${item.display}`"
                  @click="callNumber(item.dial)"
                >
                  <span class="size-4 i-lucide-phone" />
                </button>
              </div>
            </template>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
